import { Box, Slider } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { barOutOfRange, barWithinRange } from "../hooks/use-map-theme-styles";
import { maxBy } from "../utils/max-by";

interface IProps {
	min: number;
	max: number;
	values: [number, number];
	onChange: (value: [number, number]) => void;
	chartHeightPx: number;
	bars: Array<number>;
}

export const SliderWithBars: React.FC<IProps> = ({ min, max, values, chartHeightPx, bars, onChange, ...other }) => {
	const { bar } = useStyles();
	const maxBarHeight = maxBy(bars, (height) => height);
	const rangeSize = max - min;
	const barWidth = (100 / bars.length) * 0.95;
	const rangeInPercents = rangeSize ? [((values[0] - min) / rangeSize) * 100, ((values[1] - min) / rangeSize) * 100] : [0, 0];
	const gapWidthPercent = bars.length > 1 ? 5 / (bars.length - 1) : 0;

	function getBarBackground(index: number) {
		const startPosition = index * (barWidth + gapWidthPercent);
		const leftBorderWithinBar = rangeInPercents[0] - startPosition;
		const rightBorderWithinBar = rangeInPercents[1] - startPosition;
		const leftBorderPositionWithinBar = (leftBorderWithinBar / barWidth) * 100;
		const rightBorderPositionWithinBar = (rightBorderWithinBar / barWidth) * 100;

		if (leftBorderWithinBar >= 0 && leftBorderWithinBar <= barWidth && rightBorderWithinBar >= 0 && rightBorderWithinBar <= barWidth) {
			return `linear-gradient(to right, ${barOutOfRange} 0%,
				${barOutOfRange} ${leftBorderPositionWithinBar}%,
				${barWithinRange} ${leftBorderPositionWithinBar}%,
				${barWithinRange} ${rightBorderPositionWithinBar}%,
				${barOutOfRange} ${rightBorderPositionWithinBar}%,
				${barOutOfRange} 100%)`;
		}

		if (leftBorderWithinBar >= 0 && leftBorderWithinBar <= barWidth) {
			return `linear-gradient(to right, ${barOutOfRange} 0%, ${barOutOfRange} ${leftBorderPositionWithinBar}%, ${barWithinRange} ${leftBorderPositionWithinBar}%, ${barWithinRange} 100%)`;
		}

		if (rightBorderWithinBar >= 0 && rightBorderWithinBar <= barWidth) {
			return `linear-gradient(to right, ${barWithinRange} 0%, ${barWithinRange} ${rightBorderPositionWithinBar}%, ${barOutOfRange} ${rightBorderPositionWithinBar}%, ${barOutOfRange} 100%)`;
		}

		return leftBorderWithinBar < 0 && rightBorderWithinBar > barWidth ? barWithinRange : barOutOfRange;
	}

	return (
		<Box px={4} pb={2}>
			<Box display="flex" alignItems="flex-end" justifyContent="space-between" height={chartHeightPx} pb={1}>
				{bars.map((barHeight, index) => (
					<Box
						key={index}
						className={bar}
						style={{
							background: barHeight ? getBarBackground(index) : undefined,
							border: barHeight ? undefined : `1px solid ${barWithinRange}`,
						}}
						width={`${barWidth}%`}
						height={barHeight ? `${(barHeight / maxBarHeight) * 100}%` : "4px"}
					></Box>
				))}
			</Box>
			<Slider min={min} max={max} value={values} onChange={(_, values) => onChange(values as [number, number])} {...other} />
		</Box>
	);
};

const useStyles = makeStyles(() => ({
	bar: {
		borderRadius: "2px 2px 0 0",
	},
}));
